import axiosInstance, { publicAxios } from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';
import {
  AuthResponse,
  LoginCredentials,
  RegisterData,
  OtpRequest,
  OtpVerify,
  MFALoginData,
  User,
  UserProfile,
  PasswordResetRequest,
  PasswordResetConfirm,
} from '../types';

const authService = {
  /**
   * Register a new user
   */
  register: async (data: RegisterData): Promise<{ message: string }> => {
    const response = await publicAxios.post(API_ENDPOINTS.auth.register, data);
    return response.data;
  },

  /**
   * Login with email and password
   */
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await publicAxios.post(API_ENDPOINTS.auth.login, credentials);
    return response.data;
  },

  /**
   * Request OTP for login
   * @param data - Contains identifier (email or mobile number)
   */
  requestOtp: async (data: OtpRequest): Promise<{ message: string }> => {
    const response = await publicAxios.post(API_ENDPOINTS.auth.requestOtp, data);
    return response.data;
  },

  /**
   * Verify OTP and login
   */
  verifyOtp: async (data: OtpVerify): Promise<AuthResponse> => {
    const response = await publicAxios.post(API_ENDPOINTS.auth.verifyOtp, data);
    return response.data;
  },

  /**
   * Verify MFA code during login
   */
  verifyMFA: async (data: MFALoginData): Promise<AuthResponse> => {
    const response = await publicAxios.post(API_ENDPOINTS.auth.mfaLoginVerify, data);
    return response.data;
  },

  /**
   * Refresh access token using refresh token
   */
  refreshToken: async (refreshToken: string): Promise<{ access: string }> => {
    const response = await publicAxios.post(API_ENDPOINTS.auth.refreshToken, {
      refresh: refreshToken,
    });
    return response.data;
  },

  /**
   * Verify if a token is valid
   */
  verifyToken: async (token: string): Promise<boolean> => {
    try {
      await publicAxios.post(API_ENDPOINTS.auth.verifyToken, { token });
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Logout - blacklist the refresh token
   */
  logout: async (refreshToken: string): Promise<{ message: string }> => {
    const response = await axiosInstance.post(API_ENDPOINTS.auth.logout, {
      refresh: refreshToken,
    });
    return response.data;
  },

  /**
   * Get current user's profile
   */
  getProfile: async (): Promise<User> => {
    const response = await axiosInstance.get(API_ENDPOINTS.auth.profile);
    return response.data;
  },

  /**
   * Update current user's profile
   */
  updateProfile: async (data: Partial<User & { profile?: Partial<UserProfile> }>): Promise<User> => {
    const response = await axiosInstance.put(API_ENDPOINTS.auth.profile, data);
    return response.data;
  },

  /**
   * Request password reset email
   */
  requestPasswordReset: async (data: PasswordResetRequest): Promise<{ message: string }> => {
    const response = await publicAxios.post(API_ENDPOINTS.auth.requestPasswordReset, data);
    return response.data;
  },

  /**
   * Confirm password reset with token
   */
  confirmPasswordReset: async (data: PasswordResetConfirm): Promise<{ message: string }> => {
    const response = await publicAxios.post(API_ENDPOINTS.auth.confirmPasswordReset, data);
    return response.data;
  },

  /**
   * Verify email address using the signed token from the verification email
   */
  verifyEmail: async (token: string): Promise<{ message: string }> => {
    const response = await publicAxios.post(API_ENDPOINTS.auth.verifyEmail, { token });
    return response.data;
  },

  /**
   * Resend the email verification link
   */
  resendVerification: async (email: string): Promise<{ message: string }> => {
    const response = await publicAxios.post(API_ENDPOINTS.auth.resendVerification, { email });
    return response.data;
  },
};

export default authService;
